import React, { useCallback, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setFilterAdType } from "src/context/reducers/filter-settings/filterSlice";
import { useCurrentWorkspace } from "src/context/reducers/app-settings";
import { useCurrentUserQuery } from "src/context/api/auth";
import { Typography, Stack, Box, Button, IconButton, ToggleButtonGroup, ToggleButton, FormControl, Select, useTheme, MenuItem, Chip } from "@mui/material";

type HeaderProps = { alignLeft?: boolean; isAbsMode?: boolean; onToggleAbsMode?: () => void };

export const SearchTermAnalysisHeader = ({ alignLeft, isAbsMode, onToggleAbsMode }: HeaderProps): JSX.Element => {
    const dispatch = useDispatch();
    const adType: string[] = useSelector((state: any) => state.filters.adType) || [];
    const workspace: any = useCurrentWorkspace();
    const { data: currentUser } = useCurrentUserQuery();
    const isCustomer = currentUser?.role === 'customer';

    const isShoppingEnabled = workspace?.template === 'E-commerce' || workspace?.shoppingIncluded === true;

    const activeMode = useMemo<'all' | 'search' | 'shopping'>(() => {
        if (!isShoppingEnabled) return 'search';
        if (!adType || adType.length === 0) return 'all';
        if (adType.length === 1 && adType[0] === 'TXT') return 'search';
        if (adType.length === 1 && adType[0] === 'PLA') return 'shopping';
        return 'all';
    }, [adType, isShoppingEnabled]);

    useEffect(() => {
        if (isCustomer) {
            // Force "all" mode for customer role
            if (Array.isArray(adType) && adType.length > 0) {
                dispatch(setFilterAdType([]));
            }
            return;
        }
        if (!isShoppingEnabled) {
            const isAlreadySearch = Array.isArray(adType) && adType.length === 1 && adType[0] === 'TXT';
            if (!isAlreadySearch) {
                dispatch(setFilterAdType(['TXT']));
            }
        }
    }, [isShoppingEnabled, isCustomer, adType, dispatch]);

    const setMode = useCallback((mode: 'all' | 'search' | 'shopping') => {
        if (!isShoppingEnabled) {
            dispatch(setFilterAdType(['TXT']));
            return;
        }
        if (mode === 'all') dispatch(setFilterAdType([]));
        if (mode === 'search') dispatch(setFilterAdType(['TXT']));
        if (mode === 'shopping') dispatch(setFilterAdType(['PLA']));
    }, [dispatch, isShoppingEnabled]);
    const theme = useTheme()

    return (
        <Stack
            direction="row"
            alignItems="center"
            justifyContent={alignLeft ? 'flex-start' : 'flex-end'}
            spacing={1}
            sx={{ width: '100%' }}>
            {location?.pathname !== "/visibility/competition-evolution" && (
                <Stack direction="row" alignItems="center" spacing={1} sx={{ borderRadius: 1, p: '6px' }}>
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                        <Select
                            value={isAbsMode ? 'abs' : 'top'}
                            onChange={(e) => {
                                const wantAbs = e.target.value === 'abs';
                                if (typeof isAbsMode === 'boolean' && wantAbs !== isAbsMode) {
                                    onToggleAbsMode?.();
                                }
                            }}
                            displayEmpty
                            renderValue={(selected) => (
                                <Chip
                                    label={selected === 'abs' ? 'Absolute Top' : 'Top'}
                                    sx={{
                                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(145,158,171,0.20)' : 'rgba(145,158,171,0.12)',
                                        color: theme.palette.text.primary,
                                        fontSize: '13px',
                                        height: 24,
                                        borderRadius: '6px',
                                        '&:hover': {
                                            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(145,158,171,0.20)' : 'rgba(145,158,171,0.16)',
                                        },
                                        '& .MuiChip-label': {
                                            px: 1.5,
                                            lineHeight: '20px',
                                        },
                                    }}
                                />
                            )}
                sx={{
                    borderRadius: '8px',
                    '& .MuiSelect-select': {
                        padding: '0 8px'
                    },
                    height: 36,
                    fontSize: '14px',
                    '.MuiOutlinedInput-notchedOutline': {
                        border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(145,158,171,0.20)' : 'rgba(145,158,171,0.32)'}`,
                    },
                    '.MuiSelect-icon': {
                        color: theme.palette.text.primary,
                    },
                }}
                >
                <MenuItem value="top">Top</MenuItem>
                <MenuItem value="abs">Absolute Top</MenuItem>
                </Select>
                </FormControl>

                </Stack>
            )}

            {isShoppingEnabled && !isCustomer ? (
                <Box sx={{ 
                    border: `1px solid ${theme.palette.mode === 'dark' ? '#919eab33' : 'rgba(145,158,171,0.32)'}`, 
                    borderRadius: '8px', 
                    p: '6px', 
                    height: 36, 
                    display: 'flex', 
                    alignItems: 'center', 
                    bgcolor: 'transparent' 
                }}>
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={activeMode}
                        onChange={(_e, val: 'all' | 'search' | 'shopping' | null) => { if (val) setMode(val); }}
                        sx={{
                            bgcolor: 'transparent',
                            border: 'none',
                            gap: '2px',
                            '& .MuiToggleButton-root': {
                                border: 'none',
                                textTransform: 'none',
                                height: 24,
                                borderRadius: '4px',
                                px: 2,
                                color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.6)'
                            },
                            '& .MuiToggleButton-root.Mui-selected': {
                                bgcolor: theme.palette.mode === 'dark' ? '#919eab33' : 'rgba(145,158,171,0.16)',
                                color: theme.palette.text.primary
                            }
                        }}
                    >
                        <ToggleButton value="all">
                            <Typography 
                                variant="body2" 
                                fontSize="13px"
                                lineHeight="23px" 
                                fontWeight={500} 
                                whiteSpace="nowrap">
                                All
                            </Typography>
                        </ToggleButton>
                        <ToggleButton value="search">
                            <Typography 
                                variant="body2" 
                                fontSize="13px"
                                lineHeight="23px" 
                                fontWeight={500} 
                                whiteSpace="nowrap">
                                    Search 
                            </Typography>
                        </ToggleButton>
                        <ToggleButton value="shopping">
                            <Typography 
                            variant="body2" 
                            fontSize="13px" 
                            lineHeight="23px" 
                            fontWeight={500} whiteSpace="nowrap">
                                Shopping
                        </Typography>
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>
            ) : null}
        </Stack>
    );
};
